import type { ConversionRatesOvertimeListError } from "@similarweb/pro-api-client";

import { swSettings } from "app/common/services/swSettings";
import { updateFilterConsistencyCountry } from "app/routes/handlers/filter-consistency/filterConsistencyUtils";
import { WORLDWIDE_COUNTRY_ID } from "app/services/countryServiceConst";
import { proNavigator } from "single-spa/react-app/ProNavigator";

export type ConversionRateEmptyStateType = "CountryNotSupported" | "NoData";

const VALID_EMPTY_STATE_TYPES = new Set<string>(["CountryNotSupported", "NoData"]);

/**
 * Parses emptyStateType from a ResponseError's name field.
 * The backend returns 404 with a ProblemDetails JSON body where
 * emptyStateType is nested inside the Extensions object.
 * ResponseError.name stores JSON.stringify'd response body.
 */
export const parseConversionRateError = (error: unknown): ConversionRateEmptyStateType | null => {
    if (!error || typeof error !== "object") {
        return null;
    }
    const { name, status } = error as { name?: string; status?: number };
    if (status !== 404 || !name) {
        return null;
    }
    try {
        const parsed: ConversionRatesOvertimeListError = JSON.parse(name);
        const type = parsed.Extensions?.emptyStateType;
        if (typeof type === "string" && VALID_EMPTY_STATE_TYPES.has(type)) {
            return type as ConversionRateEmptyStateType;
        }
        return null;
    } catch {
        return null;
    }
};

/**
 * Resolves a target country for the "Analyze in Worldwide" CTA.
 * Prefers worldwide (999) if the user has access, otherwise falls back
 * to the first country in the user's allowed list.
 */
export const getTargetCountry = (): number => {
    if (swSettings.allowedCountry(WORLDWIDE_COUNTRY_ID)) {
        return WORLDWIDE_COUNTRY_ID;
    }
    const allowedCountries: number[] = swSettings.current?.resources?.Countries ?? [];
    return allowedCountries[0] ?? WORLDWIDE_COUNTRY_ID;
};

/**
 * Navigates to a supported country for conversion rate data.
 */
export const navigateToSupportedCountry = () => {
    const targetCountry = getTargetCountry();
    updateFilterConsistencyCountry(targetCountry);
    void proNavigator.updateParams({ country: targetCountry, isCountryChanged: null });
};
